import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faFire, faStar, faCircleCheck, faClock, faIdCard,
  faFileLines, faGift, faPassport, faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import { workVisas, confirmedSteps } from '../data/workVisas';

const WorkVisaDetail = () => {
  const { slug } = useParams();
  const v = workVisas.find((x) => x.slug === slug);

  if (!v) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Visa Not Found</h2>
          <Link to="/popular-visas" className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700">
            Back to Popular Visas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      {/* Hero */}
      <section className="relative h-80 md:h-96 bg-blue-900">
        <img src={v.image} alt={v.country} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-10 text-white">
          <Link to="/popular-visas" className="inline-flex items-center text-blue-100 hover:text-white mb-4 text-sm">
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to Popular Visas
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-5xl">{v.flag}</span>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">{v.country} Work Visa</h1>
              <p className="text-blue-100 text-lg">{v.tagline}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Latest + Popular */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <span className="inline-flex items-center bg-orange-100 text-orange-600 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                <FontAwesomeIcon icon={faFire} className="mr-1" /> Latest Visa
              </span>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{v.latestVisa.name}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{v.latestVisa.desc}</p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <span className="inline-flex items-center bg-yellow-100 text-yellow-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                <FontAwesomeIcon icon={faStar} className="mr-1" /> Most Popular Visa
              </span>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{v.popularVisa.name}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{v.popularVisa.desc}</p>
            </div>
          </div>

          {/* Confirmed visa process */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              <FontAwesomeIcon icon={faCircleCheck} className="text-green-500 mr-2" />
              Confirmed Visa Process
            </h2>
            <p className="text-gray-600 text-sm mb-6">{v.confirmedNote}</p>
            <ol className="space-y-5">
              {confirmedSteps.map((s, i) => (
                <li key={i} className="flex">
                  <span className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center mr-4">
                    {i + 1}
                  </span>
                  <div>
                    <h4 className="font-semibold text-gray-900">{s.title}</h4>
                    <p className="text-gray-600 text-sm">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* PR path */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              <FontAwesomeIcon icon={faPassport} className="text-blue-600 mr-2" />
              Path to Permanent Residence (PR)
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs text-blue-600 font-semibold uppercase mb-1">Permit validity</p>
                <p className="font-semibold text-gray-900">{v.permitValidity}</p>
              </div>
             
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              After your first permit you renew it while you keep working and living in {v.country}.
              After the required years of legal residence, you can apply for permanent / long-term
              residence. Exact conditions (language, income, time) depend on current law.
            </p>
          </div>

          {/* Requirements + benefits */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                <FontAwesomeIcon icon={faFileLines} className="text-blue-600 mr-2" /> Requirements
              </h3>
              <ul className="space-y-2">
                {v.requirements.map((r, i) => (
                  <li key={i} className="flex items-start text-gray-600 text-sm">
                    <FontAwesomeIcon icon={faCircleCheck} className="text-green-500 mt-1 mr-2" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                <FontAwesomeIcon icon={faGift} className="text-blue-600 mr-2" /> Benefits
              </h3>
              <ul className="space-y-2">
                {v.benefits.map((b, i) => (
                  <li key={i} className="flex items-start text-gray-600 text-sm">
                    <FontAwesomeIcon icon={faCircleCheck} className="text-green-500 mt-1 mr-2" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Processing times and rules are approximate and can change. Contact us for the latest
            official requirements before you apply.
          </p>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 lg:sticky lg:top-28">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Facts</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start">
                <FontAwesomeIcon icon={faIdCard} className="text-blue-600 mt-1 mr-3" />
                <div><p className="text-gray-500">Visa type</p><p className="font-semibold text-gray-900">Work Visa</p></div>
              </li>
              <li className="flex items-start">
                <FontAwesomeIcon icon={faClock} className="text-blue-600 mt-1 mr-3" />
                <div><p className="text-gray-500">Processing</p><p className="font-semibold text-gray-900">{v.processing}</p></div>
              </li>
              <li className="flex items-start">
                <FontAwesomeIcon icon={faCalendarCheck} className="text-blue-600 mt-1 mr-3" />
                <div><p className="text-gray-500">Permit validity</p><p className="font-semibold text-gray-900">{v.permitValidity}</p></div>
              </li>
              <li className="flex items-start">
                <FontAwesomeIcon icon={faPassport} className="text-blue-600 mt-1 mr-3" />
                <div><p className="text-gray-500">PR after</p><p className="font-semibold text-gray-900">{v.prAfter}</p></div>
              </li>
            </ul>
            <Link to="/contact">
              <button className="w-full mt-6 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition duration-300">
                Apply Consultation
              </button>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Other Countries</h3>
            <div className="flex flex-wrap gap-2">
              {workVisas.filter((x) => x.slug !== v.slug).map((x) => (
                <Link
                  key={x.slug}
                  to={`/popular-visas/${x.slug}`}
                  className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium border border-blue-100 hover:bg-blue-100 transition"
                >
                  {x.flag} {x.country}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default WorkVisaDetail;
